/**
 * Rosetta Operations
 * Tools for the AI agent to manage Rosetta codebase context
 */

import fs from 'fs';
import path from 'path';
import { ToolDefinition, ExecutionContext, ToolResult } from "../registry";

/**
 * Tool for initializing Rosetta in a project
 */
export const rosettaInitTool: ToolDefinition = {
  name: "rosetta_init",
  description: `Initialize Rosetta codebase context in the current project.
Use this tool when:
- You're starting to work on a codebase that doesn't have ROSETTA.md
- The user asks you to document or understand the codebase
- You want to create structured context for future sessions

This creates ROSETTA.md and .rosetta/ directory structure.`,
  parameters: {
    type: "object" as const,
    properties: {
      description: {
        type: "string",
        description: "One-sentence description of the project"
      },
      overview: {
        type: "string",
        description: "2-4 sentence overview of what the project does"
      },
      tech_stack: {
        type: "array",
        items: { type: "string" },
        description: "List of technologies (e.g., ['TypeScript', 'Next.js 14', 'PostgreSQL'])"
      },
      architecture: {
        type: "string",
        description: "ASCII diagram or description of architecture"
      },
      directory_structure: {
        type: "string",
        description: "Key directory structure with purposes"
      },
      conventions: {
        type: "array",
        items: { type: "string" },
        description: "List of coding conventions (e.g., ['camelCase for functions', 'PascalCase for components'])"
      },
      entry_points: {
        type: "array",
        items: {
          type: "object",
          properties: {
            file: { type: "string" },
            purpose: { type: "string" }
          }
        },
        description: "Key entry point files and their purposes"
      },
      gotchas: {
        type: "array",
        items: { type: "string" },
        description: "Non-obvious things that could cause issues"
      }
    },
    required: ["description", "overview"]
  },
  execute: async (params: {
    description: string;
    overview: string;
    tech_stack?: string[];
    architecture?: string;
    directory_structure?: string;
    conventions?: string[];
    entry_points?: Array<{ file: string; purpose: string }>;
    gotchas?: string[];
  }, context: ExecutionContext): Promise<ToolResult> => {
    const cwd = context.workingDirectory;
    const rosettaPath = path.join(cwd, 'ROSETTA.md');
    const rosettaDir = path.join(cwd, '.rosetta');
    const modulesDir = path.join(rosettaDir, 'modules');

    // Check if already exists
    if (fs.existsSync(rosettaPath)) {
      return {
        success: false,
        error: "ROSETTA.md already exists. Use rosetta_update to modify it.",
        metadata: { already_exists: true }
      };
    }

    try {
      // Create directories
      if (!fs.existsSync(rosettaDir)) {
        fs.mkdirSync(rosettaDir, { recursive: true });
      }
      if (!fs.existsSync(modulesDir)) {
        fs.mkdirSync(modulesDir, { recursive: true });
      }

      const today = new Date().toISOString().split('T')[0];

      // Build tech stack section
      let techStackSection = '';
      if (params.tech_stack && params.tech_stack.length > 0) {
        techStackSection = params.tech_stack.map(t => `- ${t}`).join('\n');
      } else {
        techStackSection = '- [To be documented]';
      }

      // Build conventions section
      let conventionsSection = '';
      if (params.conventions && params.conventions.length > 0) {
        conventionsSection = params.conventions.map(c => `- ${c}`).join('\n');
      } else {
        conventionsSection = '- [To be documented]';
      }

      // Build entry points table
      let entryPointsSection = '';
      if (params.entry_points && params.entry_points.length > 0) {
        entryPointsSection = params.entry_points
          .map(e => `| \`${e.file}\` | ${e.purpose} |`)
          .join('\n');
      } else {
        entryPointsSection = '| `[path]` | [purpose] |';
      }

      // Build gotchas section
      let gotchasSection = '';
      if (params.gotchas && params.gotchas.length > 0) {
        gotchasSection = params.gotchas.map(g => `- ${g}`).join('\n');
      } else {
        gotchasSection = '- [None documented yet]';
      }

      // Create ROSETTA.md content
      const rosettaContent = `# Rosetta

> ${params.description}

## Overview

${params.overview}

## Tech Stack

${techStackSection}

## Architecture

\`\`\`
${params.architecture || '[Architecture diagram to be added]'}
\`\`\`

## Directory Structure

\`\`\`
${params.directory_structure || '[Directory structure to be documented]'}
\`\`\`

## Conventions

${conventionsSection}

## Entry Points

| File | Purpose |
|------|---------|
${entryPointsSection}

## Key Patterns

[Key code patterns to be documented as you work with the codebase]

## Module Index

| Module | Path | Description | Load When |
|--------|------|-------------|-----------|
| | \`.rosetta/modules/.md\` | | |

## Gotchas

${gotchasSection}

## Agent Notes

<!--
  AGENTS: Append learnings below this line.
  Format: ### YYYY-MM-DD | agent-name
  Humans curate this section periodically.
-->

---

<!-- rosetta:version:1.0 -->
<!-- rosetta:last-updated:${today} -->
`;

      fs.writeFileSync(rosettaPath, rosettaContent, 'utf-8');

      // Create notes.md
      const notesContent = `# Agent Notes

Learnings from AI coding agents working in this codebase.

## Review Status

- Last human review: ${today}
- Entries since last review: 0

---

<!-- AGENTS: Append new entries below. Do not edit existing entries. -->
<!-- Format: ### YYYY-MM-DD | agent-name -->

### ${today} | metis-code
- Initial Rosetta context created
`;
      fs.writeFileSync(path.join(rosettaDir, 'notes.md'), notesContent, 'utf-8');

      // Create config.yml
      const configContent = `version: 1

staleness:
  warning: 30
  critical: 90

track: []

ignore:
  - "*.test.ts"
  - "*.spec.ts"
  - "__tests__/**"

modules: {}
`;
      fs.writeFileSync(path.join(rosettaDir, 'config.yml'), configContent, 'utf-8');

      return {
        success: true,
        content: `Rosetta initialized successfully!\n\nCreated:\n- ROSETTA.md (root context)\n- .rosetta/notes.md (agent learnings)\n- .rosetta/config.yml (configuration)\n\nThe codebase context will now be automatically loaded in future sessions.`,
        metadata: {
          files_created: ['ROSETTA.md', '.rosetta/notes.md', '.rosetta/config.yml']
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to initialize Rosetta: ${error.message}`
      };
    }
  }
};

/**
 * Tool for updating sections of ROSETTA.md
 */
export const rosettaUpdateTool: ToolDefinition = {
  name: "rosetta_update",
  description: `Update a section of ROSETTA.md with new information.
Use this when you learn something new about the codebase that should be documented.
You can update: overview, tech_stack, architecture, conventions, entry_points, gotchas, key_patterns`,
  parameters: {
    type: "object" as const,
    properties: {
      section: {
        type: "string",
        enum: ["overview", "tech_stack", "architecture", "directory_structure", "conventions", "entry_points", "key_patterns", "gotchas", "module_index"],
        description: "Which section to update"
      },
      content: {
        type: "string",
        description: "The new content for this section (markdown formatted)"
      }
    },
    required: ["section", "content"]
  },
  execute: async (params: { section: string; content: string }, context: ExecutionContext): Promise<ToolResult> => {
    const rosettaPath = path.join(context.workingDirectory, 'ROSETTA.md');

    if (!fs.existsSync(rosettaPath)) {
      return {
        success: false,
        error: "ROSETTA.md not found. Use rosetta_init first."
      };
    }

    try {
      let content = fs.readFileSync(rosettaPath, 'utf-8');

      // Map section names to headers
      const sectionHeaders: Record<string, string> = {
        overview: '## Overview',
        tech_stack: '## Tech Stack',
        architecture: '## Architecture',
        directory_structure: '## Directory Structure',
        conventions: '## Conventions',
        entry_points: '## Entry Points',
        key_patterns: '## Key Patterns',
        gotchas: '## Gotchas',
        module_index: '## Module Index'
      };

      const header = sectionHeaders[params.section];
      if (!header) {
        return {
          success: false,
          error: `Unknown section: ${params.section}`
        };
      }

      // Find and replace section content
      const headerRegex = new RegExp(`(${header}\\n\\n?)([\\s\\S]*?)(\\n## |\\n---)`);
      const match = content.match(headerRegex);

      if (match) {
        content = content.replace(headerRegex, `$1${params.content}\n\n$3`);
      } else {
        // Section not found, try to add it before Agent Notes
        const agentNotesIndex = content.indexOf('## Agent Notes');
        if (agentNotesIndex !== -1) {
          content = content.slice(0, agentNotesIndex) +
            `${header}\n\n${params.content}\n\n` +
            content.slice(agentNotesIndex);
        }
      }

      // Update last-updated timestamp
      const today = new Date().toISOString().split('T')[0];
      content = content.replace(
        /<!-- rosetta:last-updated:\d{4}-\d{2}-\d{2} -->/,
        `<!-- rosetta:last-updated:${today} -->`
      );

      fs.writeFileSync(rosettaPath, content, 'utf-8');

      return {
        success: true,
        content: `Updated ${params.section} section in ROSETTA.md`,
        metadata: { section: params.section }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to update ROSETTA.md: ${error.message}`
      };
    }
  }
};

/**
 * Tool for adding notes to .rosetta/notes.md
 */
export const rosettaNoteTool: ToolDefinition = {
  name: "rosetta_note",
  description: `Add a learning or discovery to .rosetta/notes.md.
Use this when you discover something non-obvious about the codebase that future sessions should know.
Examples:
- "The batch processor silently drops records over 10MB"
- "userService.sync() must be called before auth.validate()"
- "Test files must end with .test.ts, not .spec.ts"`,
  parameters: {
    type: "object" as const,
    properties: {
      note: {
        type: "string",
        description: "The learning or discovery to record"
      }
    },
    required: ["note"]
  },
  execute: async (params: { note: string }, context: ExecutionContext): Promise<ToolResult> => {
    const notesPath = path.join(context.workingDirectory, '.rosetta', 'notes.md');

    if (!fs.existsSync(notesPath)) {
      return {
        success: false,
        error: "Rosetta not initialized. Use rosetta_init first."
      };
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      let content = fs.readFileSync(notesPath, 'utf-8');

      // Append the note
      const noteEntry = `\n### ${today} | metis-code\n- ${params.note}\n`;
      content = content.trimEnd() + noteEntry;

      fs.writeFileSync(notesPath, content, 'utf-8');

      return {
        success: true,
        content: `Added note to .rosetta/notes.md: "${params.note}"`,
        metadata: { note: params.note, date: today }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to add note: ${error.message}`
      };
    }
  }
};

/**
 * Tool for creating a module documentation file
 */
export const rosettaModuleTool: ToolDefinition = {
  name: "rosetta_add_module",
  description: `Create a deep-dive documentation file for a specific module or area of the codebase.
Use this when you've analyzed a complex part of the codebase and want to document it for future sessions.`,
  parameters: {
    type: "object" as const,
    properties: {
      name: {
        type: "string",
        description: "Module name (e.g., 'auth', 'database', 'api')"
      },
      purpose: {
        type: "string",
        description: "One-sentence purpose of this module"
      },
      responsibility: {
        type: "string",
        description: "What this module does and doesn't do"
      },
      key_files: {
        type: "array",
        items: {
          type: "object",
          properties: {
            file: { type: "string" },
            purpose: { type: "string" }
          }
        },
        description: "Key files in this module"
      },
      data_flow: {
        type: "string",
        description: "How data flows through this module"
      },
      gotchas: {
        type: "array",
        items: { type: "string" },
        description: "Module-specific warnings"
      },
      load_when: {
        type: "string",
        description: "When should this module context be loaded (e.g., 'Working on authentication')"
      }
    },
    required: ["name", "purpose", "responsibility"]
  },
  execute: async (params: {
    name: string;
    purpose: string;
    responsibility: string;
    key_files?: Array<{ file: string; purpose: string }>;
    data_flow?: string;
    gotchas?: string[];
    load_when?: string;
  }, context: ExecutionContext): Promise<ToolResult> => {
    const modulesDir = path.join(context.workingDirectory, '.rosetta', 'modules');
    const modulePath = path.join(modulesDir, `${params.name}.md`);
    const rosettaPath = path.join(context.workingDirectory, 'ROSETTA.md');

    if (!fs.existsSync(modulesDir)) {
      return {
        success: false,
        error: "Rosetta not initialized. Use rosetta_init first."
      };
    }

    try {
      const today = new Date().toISOString().split('T')[0];

      // Build key files section
      let keyFilesSection = '';
      if (params.key_files && params.key_files.length > 0) {
        keyFilesSection = params.key_files
          .map(f => `| \`${f.file}\` | ${f.purpose} |`)
          .join('\n');
      } else {
        keyFilesSection = '| `[path]` | [purpose] |';
      }

      // Build gotchas section
      let gotchasSection = '';
      if (params.gotchas && params.gotchas.length > 0) {
        gotchasSection = params.gotchas.map(g => `- ${g}`).join('\n');
      } else {
        gotchasSection = '- [None documented yet]';
      }

      const moduleContent = `# Module: ${params.name.charAt(0).toUpperCase() + params.name.slice(1)}

> ${params.purpose}

<!-- rosetta:last-verified:${today} -->

## Responsibility

${params.responsibility}

## Key Files

| File | Purpose |
|------|---------|
${keyFilesSection}

## Data Flow

\`\`\`
${params.data_flow || '[Data flow to be documented]'}
\`\`\`

## Gotchas

${gotchasSection}
`;

      fs.writeFileSync(modulePath, moduleContent, 'utf-8');

      // Update Module Index in ROSETTA.md
      if (fs.existsSync(rosettaPath)) {
        let rosettaContent = fs.readFileSync(rosettaPath, 'utf-8');

        // Find the Module Index table and add entry
        const moduleIndexRegex = /(## Module Index[\s\S]*?\|[-\s|]+\|)\n/;
        const match = rosettaContent.match(moduleIndexRegex);

        if (match) {
          const newEntry = `| ${params.name} | \`.rosetta/modules/${params.name}.md\` | ${params.purpose} | ${params.load_when || 'Relevant work'} |`;
          rosettaContent = rosettaContent.replace(
            moduleIndexRegex,
            `$1\n${newEntry}\n`
          );
          fs.writeFileSync(rosettaPath, rosettaContent, 'utf-8');
        }
      }

      return {
        success: true,
        content: `Created module documentation: .rosetta/modules/${params.name}.md\nAdded to Module Index in ROSETTA.md`,
        metadata: { module: params.name, path: `.rosetta/modules/${params.name}.md` }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create module: ${error.message}`
      };
    }
  }
};

/**
 * Tool to check Rosetta status
 */
export const rosettaStatusTool: ToolDefinition = {
  name: "rosetta_status",
  description: "Check if Rosetta is initialized and get current status. Use this to see if codebase context exists.",
  parameters: {
    type: "object" as const,
    properties: {},
    required: []
  },
  execute: async (params: {}, context: ExecutionContext): Promise<ToolResult> => {
    const rosettaPath = path.join(context.workingDirectory, 'ROSETTA.md');
    const notesPath = path.join(context.workingDirectory, '.rosetta', 'notes.md');
    const modulesDir = path.join(context.workingDirectory, '.rosetta', 'modules');

    const hasRosetta = fs.existsSync(rosettaPath);

    if (!hasRosetta) {
      return {
        success: true,
        content: "Rosetta is NOT initialized in this project.\n\nTo create codebase context, use rosetta_init with project details.",
        metadata: { initialized: false }
      };
    }

    try {
      const rosettaContent = fs.readFileSync(rosettaPath, 'utf-8');

      // Get last updated
      const lastUpdatedMatch = rosettaContent.match(/<!-- rosetta:last-updated:(\d{4}-\d{2}-\d{2}) -->/);
      const lastUpdated = lastUpdatedMatch ? lastUpdatedMatch[1] : 'unknown';

      // Count modules
      let moduleCount = 0;
      if (fs.existsSync(modulesDir)) {
        moduleCount = fs.readdirSync(modulesDir).filter(f => f.endsWith('.md')).length;
      }

      // Count notes
      let noteCount = 0;
      if (fs.existsSync(notesPath)) {
        const notesContent = fs.readFileSync(notesPath, 'utf-8');
        noteCount = (notesContent.match(/^### \d{4}-\d{2}-\d{2}/gm) || []).length;
      }

      return {
        success: true,
        content: `Rosetta Status: ACTIVE\n\nLast updated: ${lastUpdated}\nModules documented: ${moduleCount}\nAgent notes: ${noteCount} entries\n\nThe codebase context is being loaded into your system prompt.`,
        metadata: {
          initialized: true,
          last_updated: lastUpdated,
          module_count: moduleCount,
          note_count: noteCount
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to check status: ${error.message}`
      };
    }
  }
};
